import { motion } from 'framer-motion';
import React from 'react';
import { FiArrowLeft, FiBriefcase, FiTarget, FiTrendingUp } from 'react-icons/fi';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { SectionHeader } from '../ui/SectionHeader';

export const Vision2030Section: React.FC = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const alignmentPoints = [
    {
      icon: <FiTarget size={28} />,
      title: 'دعم اتخاذ قرارات تعليمية مدروسة',
      description: 'نساهم في توعية الطلاب بالتخصصات المتاحة وربطها بواقع سوق العمل لاتخاذ قرارات أكثر استنارة',
      vision2030Goal: 'مواءمة مخرجات التعليم مع احتياجات سوق العمل'
    },
    {
      icon: <FiBriefcase size={28} />,
      title: 'تمكين الوصول للخبرات العملية',
      description: 'نوفر منصة تربط الطلاب بالخريجين العاملين لنقل المعرفة والخبرة العملية',
      vision2030Goal: 'رفع مشاركة السعوديين في سوق العمل'
    },
    {
      icon: <FiTrendingUp size={28} />,
      title: 'توعية بالفرص المستقبلية',
      description: 'نسلط الضوء على التخصصات الناشئة والمطلوبة في الاقتصاد الرقمي والمعرفي',
      vision2030Goal: 'تعزيز مشاركة التعليم التقني والمهني'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { 
      opacity: 1, 
      x: 0
    }
  };

  return (
    <section className="py-20 bg-gradient-subtle relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0L100 50L50 100L0 50Z' fill='%23003B5C' fill-opacity='0.1'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }} />
      </div>

      <div className="section-container relative z-10">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="max-w-lg pb-4">
            <SectionHeader
              subtitle="رؤية المملكة 2030"
              title="نساهم في برنامج تنمية القدرات البشرية"
              description="ندعم أهداف برنامج تنمية القدرات البشرية من خلال مساعدة الطلاب على اتخاذ قرارات تعليمية مدروسة تخدم سوق العمل"
              accent={true}
            />
          </div>
          <div className="text-center">
            <img 
              src="/Saudi_Vision_2030_logo.svg.png" 
              alt="Saudi Vision 2030" 
              className="mx-auto"
              width="200" 
            />
          </div>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="mt-12 lg:mt-16 grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {alignmentPoints.map((point, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="group"
            >
              <div className="card-government h-full relative overflow-hidden cursor-pointer hover:scale-105 transition-all duration-300"
                   onClick={() => window.location.href = '/services'}>
                {/* Hover Effect Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/5 to-government-navy/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-brand-primary bg-opacity-10 rounded-2xl flex items-center justify-center text-brand-primary mb-6 group-hover:scale-110 transition-transform duration-300">
                    {point.icon}
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-government-navy mb-4 font-heading">
                    {point.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {point.description}
                  </p>
                  
                  {/* Vision 2030 Connection */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-brand-primary rounded-full"></div>
                      <span className="text-brand-primary font-medium">رؤية 2030:</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 font-medium">
                      {point.vision2030Goal}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Redirect Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 text-center"
        >
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-brand-primary font-medium hover:text-brand-primary/80 transition-colors duration-300 group"
          >
            <span>كيف؟ تعرف على خدماتنا</span>
            <FiArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>

      </div>
    </section>
  );
};